/**
 * OracleLanding.jsx — LUMINA Oracle 统一问卜入口
 * Phase 98B
 *
 * 极简 AI-SaaS 风格:
 *  - 单输入框居中，打字即路由提示
 *  - 结果卡片滑入（OracleResult）
 *  - SEO 底部工具行保留
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../constants/config";
import OracleResult from "../components/OracleResult";

// ─── 颜色常量 ──────────────────────────────────────────────────────────────────
const GD  = "#D4AF37";
const GDH = "#B8960C";
const BG  = "#FDFBF7";

// ─── 实时路由提示（纯关键词，不调后端）──────────────────────────────────────
const HINT_RULES = [
  { keywords: ["梦见","梦到","做了个梦","噩梦","梦里","昨晚梦"], hint: "💭 将使用：周公解梦（259条词典 · 秒级响应）" },
  { keywords: ["起名","取名","宝宝名","测名","改名"], hint: "✍️ 将使用：五格剖象起名（笔画数理 · 五行补缺）" },
  { keywords: ["今天","今日","黄历","宜忌","吉日","黄道"], hint: "📅 将使用：老黄历（节气+建除十二神）" },
  { keywords: ["抽签","求签","灵签","观音"], hint: "🙏 将使用：观音灵签（100签 · 签诗解读）" },
  { keywords: ["我的命","命盘","格局","一辈子","天赋","命运","命格","宿命"], hint: "🔮 将使用：八字+紫微（需要您的生辰信息）" },
  { keywords: ["会不会","能不能","结果","顺利吗","成功吗","合适吗","要不要","签约","面试","展会","生意","投资"], hint: "⚡ 将使用：六爻起卦（无需生辰 · 3秒出结果）" },
  { keywords: ["修行","法门","密法","本尊","灌顶","修持","修什么","气脉","拙火","前行","度母","普巴","上师","那若","法会","扎龙","仁波切","荟供","闭关","禅修","法讯","朗日"], hint: "☸️ 将使用：密法修行建议（命理→本尊·五根柱子框架）" },
];

function detectHint(text) {
  for (const rule of HINT_RULES) {
    if (rule.keywords.some(kw => text.includes(kw))) {
      return rule.hint;
    }
  }
  if (text.length > 4) return "⚡ 将使用：六爻起卦（无需生辰 · 3秒出结果）";
  return "";
}

// ─── 循环 placeholder（暗示四种术数都存在）────────────────────────────────
const PLACEHOLDERS = [
  "🔮 八字推流年：我明年适合换工作吗？",
  "🌌 紫微斗数：这段感情是否有正缘？",
  "⚡ 六爻起卦：去上海参展能接到大单吗？",
  "🌀 奇门遁甲：新办公室选哪个方位最旺财？",
];

// ─── loading 步骤动画文案 ────────────────────────────────────────────────────
const LOADING_STEPS = [
  { emoji: "🧭", text: "正在分析提问意图" },
  { emoji: "🎯", text: "匹配最合适的术数方法" },
  { emoji: "⚙️", text: "排盘计算中" },
  { emoji: "🔍", text: "生成解读" },
];

// ─── 跑马灯 ticker 条目 ────────────────────────────────────────────────────
const TICKER_FALLBACK = [
  "⟡ 等待第一位用户提问… 八字/六爻/紫微/奇门 四大引擎就绪",
];

// ─── 内容过滤 ──────────────────────────────────────────────────────────────────
// 禁止政治/国家层面的提问，超级用户 (jianguang.xu@gmail.com) 不设限制
const SUPER_USER_EMAIL = "jianguang.xu@gmail.com";

const BLOCKED_KEYWORDS = [
  // 政治领导 / 政权
  "习近平", "李克强", "胡锦涛", "温家宝", "江泽民", "朱镕基", "邓小平", "毛泽东", "周恩来",
  "主席", "总书记", "总理", "国家主席", "政治局", "中央", "常委", "国务院",
  "领导人", "政权", "换届",
  // 政党 / 体制
  "共产党", "中共", "国民党", "民进党", "民主党", "共和党",
  // 政治运动 / 敏感事件
  "六四", "天安门", "法轮功", "八九",
  // 领土 / 主权
  "台湾独立", "台独", "藏独", "疆独", "港独", "香港独立", "西藏独立", "新疆独立",
  "台湾", "独立",
  // 国家政策 / 宏观政治
  "国家政策", "国策", "政治", "体制", "政变", "革命", "造反",
  "选举", "大选", "立法", "人大", "政协", "外交部",
  "领土", "主权", "统一", "分裂",
  "中国",
  // 敏感组织
  "反华", "反中", "反共",
];

// 允许的个人领域关键词 — 即使触碰以上词，如果是个人语境也放行
const PERSONAL_CONTEXT_WHITELIST = [
  "我", "我的", "个人", "自己", "自家", "家里",
];

function decodeJWTEmail(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    // base64url decode payload
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.email || payload.sub || null;
  } catch (_) {
    return null;
  }
}

function isSuperUser() {
  const token = localStorage.getItem("lumina_token");
  if (!token) return false;
  const email = decodeJWTEmail(token);
  return email === SUPER_USER_EMAIL;
}

function isBlockedContent(text) {
  const q = text.toLowerCase();
  // 检查是否有个人语境 — 如果问题明显是问自己的，放行
  const hasPersonalContext = PERSONAL_CONTEXT_WHITELIST.some(kw => q.includes(kw));

  for (const kw of BLOCKED_KEYWORDS) {
    if (q.includes(kw.toLowerCase())) {
      // 如果明显是个人问题语境（如"我在政府工作，我的事业…"），放行
      if (hasPersonalContext) continue;
      return kw; // 返回触发的关键词
    }
  }
  return null;
}

// ─── 真实案例 ─────────────────────────────────────────────────────────────────
const EXAMPLES = [
  { q: "过几天去上海展会卖货，结果如何？", tag: "六爻起卦", note: "降低预期，拿联系方式" },
  { q: "我最近梦见牙齿掉了是什么意思？", tag: "周公解梦", note: "脱落意象 · 关注亲人健康" },
  { q: "今天适合签合同吗？", tag: "老黄历", note: "查日柱宜忌" },
];

// ─── Main Component ──────────────────────────────────────────────────────────

export default function OracleLanding({ lang = "zh" }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const composingRef = useRef(false);  // true = IME composing, textarea uncontrolled

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // 循环 placeholder
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPlaceholderIdx(i => (i + 1) % PLACEHOLDERS.length), 3000);
    return () => clearInterval(t);
  }, []);

  const [query, setQuery]       = useState("");
  const [hint, setHint]         = useState("");
  const [loading, setLoading]   = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState("");
  const [glow,    setGlow]    = useState({ x: 50, y: 50 });  // 日照金山光标跟踪
  const [loadingStep, setLoadingStep] = useState(-1);
  const [mode, setMode] = useState("oracle");  // oracle | decision | breathe
  const askCountRef = useRef(0);  // 本会话追问次数

  // 跑马灯 — API 轮询真实数据，30s刷新
  const [tickerItems, setTickerItems] = useState(TICKER_FALLBACK);
  const [tickerIdx, setTickerIdx] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchTicker = async () => {
      try {
        const resp = await fetch(`${API}/api/v4/omni/recent-queries?limit=10`);
        const data = await resp.json();
        if (!cancelled && data.ok && data.items?.length) {
          setTickerItems(data.items);
        }
      } catch (_) {}
    };
    fetchTicker();
    const interval = setInterval(fetchTicker, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);
  useEffect(() => {
    if (tickerItems.length <= 1) return;
    const t = setInterval(() => setTickerIdx(i => (i + 1) % tickerItems.length), 5000);
    return () => clearInterval(t);
  }, [tickerItems]);

  // Safety: if compositionStart fired but compositionEnd never did, auto-reset
  useEffect(() => {
    if (!composingRef.current) return;
    const t = setTimeout(() => { composingRef.current = false; }, 4000);
    return () => clearTimeout(t);
  });

  // 自动聚焦
  useEffect(() => { inputRef.current?.focus(); }, []);

  // 实时路由提示（IME合成期间不拦截 — textarea在合成期间是非受控的，onChange可以安全更新state）
  const handleInput = useCallback((e) => {
    const v = e.target.value;
    setQuery(v);
    setHint(v.trim().length > 1 ? detectHint(v) : "");
    if (result) setResult(null); // 清空旧结果
    setError("");
  }, [result]);

  // 提交
  const handleSubmit = useCallback(async (q = query, forcedModule = "auto") => {
    const text = q.trim();
    if (!text) return;

    // ── 内容审查：禁止政治/国家层面提问 ──
    if (!isSuperUser()) {
      const blocked = isBlockedContent(text);
      if (blocked) {
        setError("抱歉，我们专注于个人能量咨询（感情、婚姻、事业、健康、财运等），暂不支持国家或政治层面的问题。");
        return;
      }
    }

    setLoading(true);
    setError("");
    setResult(null);
    setLoadingStep(0);

    // 进度动画
    const stepTimer = setInterval(() => {
      setLoadingStep(s => s < LOADING_STEPS.length - 1 ? s + 1 : s);
    }, 600);

    try {
      const token = localStorage.getItem("lumina_token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const endpoint = mode === "breathe"
        ? `${API}/api/v4/omni/breathe`
        : `${API}/api/v4/omni/query`;

      const body = { query_text: text, wx_id: "", forced_module: forcedModule };
      if (mode === "decision") body.decision_mode = true;

      const resp = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `请求失败 (${resp.status})`);
      }

      const data = await resp.json();
      setResult(data);
      askCountRef.current += 1;

      // 3次追问后温和引导
      if (askCountRef.current >= 3) {
        setTimeout(() => {
          setResult(prev => prev ? {
            ...prev,
            upsell: { show_pdf: true, reason: "想更深入了解？系统可以为您生成完整的命理报告。" }
          } : prev);
        }, 2000);
      }
    } catch (e) {
      setError(e.message || "网络错误，请稍后重试");
    } finally {
      clearInterval(stepTimer);
      setLoadingStep(-1);
      setLoading(false);
    }
  }, [query, mode]);

  const handleKeyDown = (e) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleExample = (q) => {
    setQuery(q);
    setHint(detectHint(q));
    setTimeout(() => handleSubmit(q), 0);
  };

  // 追问（清空结果 → 保留输入框）
  const handleFollowUp = useCallback((q, forcedModule = "auto") => {
    setQuery(q);
    handleSubmit(q, forcedModule);
  }, [handleSubmit]);

  return (
    <div style={{ minHeight: "100vh", background: BG, fontFamily: "'Noto Sans SC','Inter',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;500;700&family=Inter:wght@400;500&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Ma+Shan+Zheng&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }

        @keyframes fadeIn { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin { to{transform:rotate(360deg)} }

        .oracle-example:hover { border-color: ${GD}!important; background: rgba(212,175,55,.06)!important; cursor: pointer; }
        .oracle-hint { animation: fadeIn .25s ease; }
      `}</style>

      {/* ── Hero (Landing Page 暖色山水风格) ──────────────────────────────────── */}
      <section
        onMouseMove={e=>{
          const r=e.currentTarget.getBoundingClientRect();
          setGlow({x:Math.round((e.clientX-r.left)/r.width*100),y:Math.round((e.clientY-r.top)/r.height*100)});
        }}
        style={{
          position:"relative",minHeight:"100vh",
          display:"flex",flexDirection:"column",
          alignItems:"center",justifyContent:"center",overflow:"hidden",
        }}>
        {/* BG image */}
        <div style={{
          position:"absolute",inset:0,zIndex:0,
          backgroundImage: `url("/kailash.jpg")`,
          backgroundSize:"cover",backgroundPosition:"center 58%",
          filter:"brightness(1.08) saturate(1.18) contrast(1.04)",
          pointerEvents:"none",
        }}/>
        {/* Overlay: top darkening for text readability */}
        <div style={{position:"absolute",inset:0,zIndex:1,
          background:"linear-gradient(180deg,rgba(0,0,0,.20) 0%,rgba(0,0,0,.10) 40%,rgba(0,0,0,.02) 65%,transparent 85%)",
          pointerEvents:"none"}}/>
        {/* Golden horizon bloom */}
        <div style={{position:"absolute",inset:0,zIndex:2,
          background:"radial-gradient(ellipse 85% 46% at 50% 60%,rgba(230,180,40,.26) 0%,rgba(200,130,20,.08) 52%,transparent 70%)",
          pointerEvents:"none"}}/>
        {/* Cursor glow — 日照金山 */}
        <div style={{
          position:"absolute",inset:0,zIndex:4,pointerEvents:"none",
          background:`radial-gradient(circle 580px at ${glow.x}% ${glow.y}%,rgba(212,175,55,0.11) 0%,rgba(180,130,20,0.05) 45%,transparent 70%)`,
          transition:"background 0.18s ease",
        }}/>

        {/* ── Content ── */}
        <div style={{position:"relative",zIndex:10,textAlign:"center",padding:"0 24px",maxWidth:800}}>

          {/* Headline */}
          <h1 style={{
            fontSize:"clamp(28px,5vw,44px)",fontWeight:600,color:"#fff",
            letterSpacing:".08em",lineHeight:1.3,marginBottom:8,
            fontFamily:"'Noto Sans SC','Inter',sans-serif",
            textShadow:"0 2px 24px rgba(0,0,0,.7)",
          }}>
            {lang === "en" ? "You don't need to understand energy" : "你不需要懂能量"}
          </h1>
          <div style={{
            fontSize:"clamp(24px,4vw,38px)",fontWeight:600,color:GD,
            letterSpacing:".08em",marginBottom:32,
            fontFamily:"'Noto Sans SC','Inter',sans-serif",
            textShadow:"0 2px 20px rgba(0,0,0,.7)",
          }}>
            {lang === "en" ? "You only need to ask questions" : "你只需要问问题"}
          </div>
          <div style={{
            fontSize:"clamp(13px,2vw,15px)",color:"rgba(255,255,255,.65)",
            marginBottom:32,fontWeight:400,letterSpacing:".04em",
          }}>
            {lang === "en" ? "Decision support · Emotional outlet — Not to predict, but to illuminate" : "决策辅助 · 情绪出口 — 不为预知，为照见"}
          </div>

          {/* ── Oracle 输入框 (glass-morphism) ── */}
          <div style={{maxWidth:560,margin:"0 auto 16px"}}>
            <div style={{
              background:"rgba(0,0,0,.35)",backdropFilter:"blur(12px)",
              border:"1px solid rgba(212,175,55,.30)",borderRadius:14,
              padding:"14px 16px",boxShadow:"0 4px 32px rgba(0,0,0,.25)",
            }}>
              <textarea
                ref={inputRef}
                value={composingRef.current ? undefined : query}
                onChange={handleInput}
                onCompositionStart={()=>{composingRef.current=true;}}
                onCompositionEnd={e=>{composingRef.current=false;const v=e.target.value;setQuery(v);setHint(v.trim().length>1?detectHint(v):"");if(result)setResult(null);setError("");}}
                onBlur={()=>{composingRef.current=false;}}
                onKeyDown={handleKeyDown}
                placeholder={PLACEHOLDERS[placeholderIdx]}
                rows={2}
                style={{
                  width:"100%",border:"none",background:"transparent",
                  color:"#fff",fontSize:16,resize:"none",outline:"none",
                  fontFamily:"'Noto Sans SC','PingFang SC','Microsoft YaHei',sans-serif",
                }}
              />
              {hint && (
                <div className="oracle-hint" style={{fontSize:12,color:"rgba(212,175,55,.85)",marginTop:8,fontWeight:500}}>
                  {hint}
                </div>
              )}
              <div style={{fontSize:11,color:"rgba(255,255,255,.35)",marginTop:6}}>
                🆓 每日免费 1 次 · 登录后不限次
              </div>
              <div style={{fontSize:11,color:"rgba(255,255,255,.30)",marginTop:2}}>
                🔒 请描述您的困惑即可，无需重复输入真实姓名与生辰
              </div>
              {loading && loadingStep >= 0 && (
                <div style={{marginTop:8}}>
                  {LOADING_STEPS.map((step,i)=>(
                    <div key={i} style={{
                      display:"flex",alignItems:"center",gap:8,
                      fontSize:12,color:i<=loadingStep?GD:"rgba(255,255,255,.3)",
                      fontWeight:i<=loadingStep?500:400,
                      padding:"2px 0",transition:"color .3s,font-weight .3s",
                    }}>
                      <span>{i<=loadingStep?step.emoji:"○"}</span>
                      <span>{step.text}</span>
                      {i<=loadingStep&&<span style={{color:"#059669",fontSize:10,marginLeft:"auto"}}>✔</span>}
                    </div>
                  ))}
                </div>
              )}
              {error && (
                <div style={{color:"#FCA5A5",fontSize:12,marginTop:8}}>{error}</div>
              )}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:10}}>
                <div style={{display:"flex",gap:6}}>
                  {[
                    {key:"oracle", label:"🔮 问事", title:"八字/六爻/紫微/奇门 · 传统问卜"},
                    {key:"decision", label:"🧭 决策", title:"不判吉凶 · 分析利弊 · 看清选项"},
                    {key:"breathe", label:"🌿 急救", title:"情绪陪伴 · CBT话术 · 不需要生辰"},
                  ].map(m => (
                    <button key={m.key}
                      onClick={() => { setMode(m.key); setResult(null); setError(""); inputRef.current?.focus(); }}
                      disabled={loading}
                      title={m.title}
                      style={{
                        padding: "4px 10px", borderRadius: 14,
                        fontSize: 11, fontWeight: mode === m.key ? 600 : 400,
                        border: mode === m.key ? "1px solid rgba(212,175,55,.6)" : "1px solid rgba(255,255,255,.15)",
                        background: mode === m.key ? "rgba(212,175,55,.15)" : "rgba(255,255,255,.06)",
                        color: mode === m.key ? GD : "rgba(255,255,255,.55)",
                        cursor: loading ? "default" : "pointer",
                        transition: "all .2s",
                        fontFamily: "'Noto Sans SC','Inter',sans-serif",
                        letterSpacing: ".03em",
                      }}
                    >{m.label}</button>
                  ))}
                </div>
                <button
                  onClick={()=>handleSubmit()}
                  disabled={loading||!query.trim()}
                  style={{
                    background:loading||!query.trim()?"#6B7280":"linear-gradient(135deg,#D4AF37,#064E3B)",
                    color:"#fff",border:"none",borderRadius:8,
                    padding:"10px 28px",fontSize:14,fontWeight:600,
                    cursor:loading||!query.trim()?"default":"pointer",
                    letterSpacing:".04em",
                    fontFamily:"'Noto Sans SC','Inter',sans-serif",
                  }}>
                  {loading?"…":"提问 →"}
                </button>
              </div>
            </div>
          </div>

          {/* Trust bar */}
          <div style={{
            display:"flex",justifyContent:"center",gap:24,flexWrap:"wrap",
            fontSize:12,color:"rgba(255,255,255,.55)",
            fontFamily:"'Inter',sans-serif",letterSpacing:".03em",
          }}>
            <span>🔮 四引擎 · 六爻/八字/紫微/奇门</span>
            <span>🆓 免费试用 · 无需登录</span>
          </div>
        </div>

        {/* Scroll arrow */}
        <div style={{
          position:"absolute",bottom:24,left:"50%",
          color:"rgba(212,180,80,.4)",fontSize:20,zIndex:10,cursor:"pointer",
        }} onClick={()=>{const el=document.getElementById("oracle-below");if(el)el.scrollIntoView({behavior:"smooth"});}}>↓</div>
      </section>

      <div id="oracle-below" style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px 20px" }}>

        {/* ── 结果卡片 ──────────────────────────────────────────────────────── */}
        {result && (
          <OracleResult
            result={result}
            originalQuery={query}
            onFollowUp={handleFollowUp}
            onNavigate={navigate}
            onNewQuestion={()=>{setResult(null);setError("");setTimeout(()=>inputRef.current?.scrollIntoView({behavior:"smooth",block:"center"}),100);}}
          />
        )}

        {/* ── 真实案例 ─────────────────────────────────────────────────────── */}
        {!result && (
          <div style={{ marginTop: 32 }}>
            <div style={{ fontSize: 13, color: "#94A3B8", marginBottom: 12, textAlign: "center" }}>
              真实案例
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {EXAMPLES.map((ex, i) => (
                <div
                  key={i}
                  className="oracle-example"
                  onClick={() => handleExample(ex.q)}
                  style={{
                    border: "1.5px solid #E2E8F0", borderRadius: 10,
                    padding: "12px 16px", background: "#fff",
                    transition: "all .15s",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 12, background: `rgba(212,175,55,.15)`, color: GDH, borderRadius: 4, padding: "2px 7px", fontWeight: 500 }}>
                      {ex.tag}
                    </span>
                  </div>
                  <div style={{ fontSize: 14, color: "#334155", marginBottom: 4 }}>"{ex.q}"</div>
                  <div style={{ fontSize: 12, color: "#94A3B8" }}>→ {ex.note}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── 实时跑马灯 ──────────────────────────────────────────────────────── */}
        <div style={{ marginTop: 36, textAlign: "center", overflow: "hidden" }}>
          <div style={{
            fontSize: 11, color: "#94A3B8",
            animation: "fadeIn .6s ease",
            whiteSpace: "nowrap",
          }}>
            {tickerItems[tickerIdx]}
          </div>
        </div>

        {/* ── SEO 底部工具行 ──────────────────────────────────────────────── */}
        <div style={{ marginTop: 48, padding: "20px 0 16px", borderTop: "1px solid #E2E8F0", textAlign: "center" }}>
          <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 10 }}>免费工具</div>
          <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
            {[
              ["📅 老黄历", "/almanac"],
              ["🙏 观音灵签", "/oracle"],
              ["✍️ 在线起名", "/naming"],
              ["💭 解梦", "/dreams"],
            ].map(([label, path]) => (
              <a
                key={path}
                href={path}
                style={{ fontSize: 13, color: "#64748B", textDecoration: "none", padding: "4px 10px",
                  border: "1px solid #E2E8F0", borderRadius: 6, background: "#fff" }}
              >
                {label}
              </a>
            ))}
          </div>

          {/* 隐藏 SEO 关键词 */}
          <div style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", opacity: 0 }}>
            六爻占卜、八字算命、紫微斗数、奇门遁甲、在线算命、免费算命、AI算命、命理分析、
            易经占卜、梅花易数、周易预测、运势查询
          </div>
        </div>

        {/* 品牌背书 */}
        <div style={{ textAlign: "center", padding: "8px 0 32px", fontSize: 12, color: "#94A3B8" }}>
          八字是真排盘 · 六爻是真摇卦 · 紫微是真算星
          <br />
          种子确定则卦象确定 — 不是概率编造
        </div>
      </div>
    </div>
  );
}
